import { useEffect, useState, type ReactNode } from "react";

/**
 * FilterBar — horizontální pruh toggle-pilulek (buttonů).
 *
 * Používá se na SearchPage pro rok a kategorii. Zahrnuje "Vše" toggle
 * pro reset konkrétní skupiny filtrů.
 *
 * Podporuje zkrácený režim: pokud je `collapseAfter` nastaveno a options
 * je víc, zobrazí se "+ N dalších" toggle pro rozbalení zbytku.
 * Když je aktivní hodnota mimo visible set, expand se provede automaticky.
 */

export interface FilterOption<T extends string | number> {
  value: T;
  label: string;
  count?: number;
}

export function FilterBar<T extends string | number>({
  label,
  options,
  value,
  onChange,
  allLabel = "Vše",
  collapseAfter,
}: {
  label: string;
  options: FilterOption<T>[];
  value: T | null;
  onChange: (v: T | null) => void;
  allLabel?: string;
  collapseAfter?: number;
}): JSX.Element {
  const canCollapse =
    typeof collapseAfter === "number" && options.length > collapseAfter;
  const [expanded, setExpanded] = useState(!canCollapse);

  // Pokud se vybere hodnota, která je za limitem, automaticky rozbalit
  useEffect(() => {
    if (!canCollapse || value === null || expanded) return;
    const idx = options.findIndex((o) => o.value === value);
    if (idx >= (collapseAfter ?? 0)) setExpanded(true);
  }, [canCollapse, value, options, expanded, collapseAfter]);

  const visible =
    canCollapse && !expanded ? options.slice(0, collapseAfter) : options;
  const hidden = canCollapse && !expanded ? options.length - visible.length : 0;

  return (
    <div className="flex items-start gap-3">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-400 shrink-0 w-20 pt-1.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Pill active={value === null} onClick={() => onChange(null)}>
          {allLabel}
        </Pill>
        {visible.map((opt) => (
          <Pill
            key={String(opt.value)}
            active={value === opt.value}
            onClick={() => onChange(opt.value === value ? null : opt.value)}
          >
            {opt.label}
            {opt.count !== undefined && (
              <span className="ml-1.5 text-[10px] text-zinc-400 tabular-nums">
                {opt.count}
              </span>
            )}
          </Pill>
        ))}
        {canCollapse && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center h-11 sm:h-8 px-3 rounded-full text-sm font-medium bg-transparent text-zinc-500 hover:text-ktm-orange hover:bg-ktm-orange-50 transition-colors"
          >
            + {hidden} dalších
          </button>
        )}
        {canCollapse && expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="inline-flex items-center h-11 sm:h-8 px-3 rounded-full text-sm font-medium bg-transparent text-zinc-500 hover:text-ktm-orange hover:bg-ktm-orange-50 transition-colors"
          >
            Sbalit
          </button>
        )}
      </div>
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={(e) => {
        onClick();
        // Odstraní focus-ring po kliku myší — zůstává jen pro tab-navigaci
        (e.currentTarget as HTMLButtonElement).blur();
      }}
      className={`inline-flex items-center h-11 sm:h-8 px-3 rounded-full text-sm font-medium transition-colors duration-100 ${
        active
          ? "bg-ktm-black text-white hover:bg-zinc-800"
          : "bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
      }`}
    >
      {children}
    </button>
  );
}
